import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import Flag from 'react-world-flags';
import logo from '../images/logo.png';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [langMenuTimeout, setLangMenuTimeout] = useState(null);
  const { language, setLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const languages = [
    { code: 'sk', name: 'SK', flagCode: 'sk' },
    { code: 'pl', name: 'PL', flagCode: 'pl' },
    { code: 'en', name: 'EN', flagCode: 'gb' },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  const scheduleLangMenuClose = () => {
    if (langMenuTimeout) {
      clearTimeout(langMenuTimeout);
    }
    const timeoutId = setTimeout(() => {
      setIsLangMenuOpen(false);
    }, 5000);
    setLangMenuTimeout(timeoutId);
  };

  useEffect(() => {
    return () => {
      if (langMenuTimeout) {
        clearTimeout(langMenuTimeout);
      }
    };
  }, [langMenuTimeout]);

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 border-b ${
      isScrolled 
        ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg border-gray-200 dark:border-gray-800' 
        : 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-gray-200 dark:border-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src={logo} 
              alt="Chata Barvinok Logo" 
              className="h-16 w-16 object-contain dark:brightness-0 dark:invert"
            />
            <span className="text-gray-900 dark:text-white font-bold text-lg tracking-wide">CHATA BARVINOK</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors uppercase tracking-wide ${
                isActive('/') ? 'text-copper-400' : 'text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300'
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/ubytovanie"
              className={`text-sm font-medium transition-colors uppercase tracking-wide ${
                isActive('/ubytovanie') ? 'text-copper-400' : 'text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300'
              }`}
            >
              {t('nav.accommodation')}
            </Link>
            <Link
              to="/rezort"
              className={`text-sm font-medium transition-colors uppercase tracking-wide ${
                isActive('/rezort') ? 'text-copper-400' : 'text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300'
              }`}
            >
              {t('nav.resort')}
            </Link>
            <Link
              to="/galeria"
              className={`text-sm font-medium transition-colors uppercase tracking-wide ${
                isActive('/galeria') ? 'text-copper-400' : 'text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300'
              }`}
            >
              {t('nav.gallery')}
            </Link>
            <Link
              to="/ubytovanie#pricing-section"
              className={`text-sm font-medium transition-colors uppercase tracking-wide ${
                location.pathname === '/ubytovanie' && location.hash === '#pricing-section' ? 'text-copper-400' : 'text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300'
              }`}
            >
              {t('accommodation.pricing')}
            </Link>
            <Link
              to="/kontakt"
              className={`text-sm font-medium transition-colors uppercase tracking-wide ${
                isActive('/kontakt') ? 'text-copper-400' : 'text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300'
              }`}
            >
              {t('nav.contact')}
            </Link>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="text-gray-700 dark:text-white hover:text-copper-400 transition-colors p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800/50"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                // Sun icon for light mode
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                // Moon icon for dark mode
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {/* Language Selector */}
            <div 
              className="relative"
              onMouseLeave={scheduleLangMenuClose}
              onMouseEnter={() => {
                if (langMenuTimeout) {
                  clearTimeout(langMenuTimeout);
                  setLangMenuTimeout(null);
                }
              }}
            >
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center space-x-2 text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300 transition-colors uppercase tracking-wide"
              >
                <Flag 
                  code={languages.find(l => l.code === language)?.flagCode} 
                  className="w-5 h-3 object-cover rounded-sm"
                />
                <span>{language.toUpperCase()}</span>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-20 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsLangMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center space-x-2 ${
                        language === lang.code ? 'text-copper-400' : 'text-gray-700 dark:text-white'
                      }`}
                    >
                      <Flag 
                        code={lang.flagCode} 
                        className="w-5 h-3 object-cover rounded-sm"
                      />
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-4">
            {/* Theme Toggle Mobile */}
            <button
              onClick={toggleTheme}
              className="text-gray-700 dark:text-white hover:text-copper-400 transition-colors p-2 rounded-lg"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            
            {/* Language Selector Mobile */}
            <div className="relative">
              <button
                onClick={() => {
                  setIsLangMenuOpen((prev) => {
                    const next = !prev;
                    if (next) {
                      scheduleLangMenuClose();
                    } else if (langMenuTimeout) {
                      clearTimeout(langMenuTimeout);
                    }
                    return next;
                  });
                }}
                className="text-gray-700 dark:text-white uppercase flex items-center space-x-2"
              >
                <Flag 
                  code={languages.find(l => l.code === language)?.flagCode} 
                  className="w-5 h-3 object-cover rounded-sm"
                />
                <span>{language.toUpperCase()}</span>
              </button>
              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-20 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsLangMenuOpen(false);
                        if (langMenuTimeout) {
                          clearTimeout(langMenuTimeout);
                        }
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center space-x-2 ${
                        language === lang.code ? 'text-copper-400' : 'text-gray-700 dark:text-white'
                      }`}
                    >
                      <Flag 
                        code={lang.flagCode} 
                        className="w-5 h-3 object-cover rounded-sm"
                      />
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-700 dark:text-white"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
          <div className="px-6 py-4 space-y-3">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300 font-medium py-2 uppercase tracking-wide ${
                isActive('/') ? 'text-copper-400' : ''
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/ubytovanie"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300 font-medium py-2 uppercase tracking-wide ${
                isActive('/ubytovanie') ? 'text-copper-400' : ''
              }`}
            >
              {t('nav.accommodation')}
            </Link>
            <Link
              to="/rezort"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300 font-medium py-2 uppercase tracking-wide ${
                isActive('/rezort') ? 'text-copper-400' : ''
              }`}
            >
              {t('nav.resort')}
            </Link>
            <Link
              to="/galeria"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300 font-medium py-2 uppercase tracking-wide ${
                isActive('/galeria') ? 'text-copper-400' : ''
              }`}
            >
              {t('nav.gallery')}
            </Link>
            <Link
              to="/ubytovanie#pricing-section"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300 font-medium py-2 uppercase tracking-wide ${
                location.pathname === '/ubytovanie' && location.hash === '#pricing-section' ? 'text-copper-400' : ''
              }`}
            >
              {t('accommodation.pricing')}
            </Link>
            <Link
              to="/kontakt"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block text-gray-700 dark:text-white hover:text-copper-500 dark:hover:text-silver-300 font-medium py-2 uppercase tracking-wide ${
                isActive('/kontakt') ? 'text-copper-400' : ''
              }`}
            >
              {t('nav.contact')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;

